import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useApiClient } from '../../api/ApiClientContext';
import styles from './LeaderboardPage.module.scss';
import layoutStyles from '../../styles/sidebarLayout.module.scss';
import TeamProgressDashboard from '../../components/leaderboard/TeamProgressDashboard';
import MoodCard from '../../components/moodCard';
import { DashboardResponse, EndorsementDirection, MilestonesResponse, UserFlowStatusResponse } from '../../api/Api';
import { BarChart, LineChart, ScatterChart } from '@mantine/charts';
import { Icon } from '../../assets/icons/Icon';
import { RingProgress, Text } from '@mantine/core';
import { Calendar } from '@mantine/dates';
import dayjs from 'dayjs';
import { QuickInsightCard } from '../../components/habitsDashboard/OrganisationalObjectives';
import insightStyles from '../../components/habitsDashboard/OrganisationalObjectives/OrganisationalObjectives.module.scss';
import { EndorsementsDrawer } from '../../components/endorsementsDrawer/EndorsementsDrawer';
import { PositiveImpactsDrawer } from '../../components/positiveImpactsDrawer/PositiveImpactsDrawer';
import InitiativeInsights from '../../components/initiativeInsights/InitiativeInsights';
import { OrganisationInsights } from '../../components/organisationInsights/OrganisationInsights';

interface StreakRange {
  start: string;
  end: string;
  logged_days: number;
}

const StreakCalendar: React.FC = () => {
  const apiClient = useApiClient();
  const [displayedMonth, setDisplayedMonth] = useState<Date>(new Date());

  const daysToFetch = useMemo(() => {
    const today = dayjs();
    const startOfDisplayedMonth = dayjs(displayedMonth).startOf('month');
    const diff = today.diff(startOfDisplayedMonth, 'day');
    return Math.max(diff + 31, 60);
  }, [displayedMonth]);

  const { data: progressData } = useQuery({
    queryKey: ['calendarProgress', daysToFetch],
    queryFn: async () => {
      const res = await apiClient.api.getProgressChartDataApiV1DashboardProgressGet({ days: daysToFetch });
      return res?.data;
    },
  });

  const dailyCompletion: Record<string, boolean> = progressData?.daily_completion || {};
  const streakRanges: StreakRange[] = (progressData?.overall_stats as any)?.streak_ranges || [];

  const isInAnyRange = (dateStr: string): boolean => {
    return streakRanges.some((r) => dateStr >= r.start && dateStr <= r.end);
  };

  const isLoggedInRange = (dateStr: string): boolean => {
    return dailyCompletion[dateStr] === true && isInAnyRange(dateStr);
  };

  const getDayProps = (date: string) => {
    const dateStr = dayjs(date).format('YYYY-MM-DD');
    const logged = dailyCompletion[dateStr] === true;

    const isToday = dayjs(date).isSame(dayjs(), 'day');
    const todayBorder = isToday ? { border: '2px solid #4C6EF5' } : {};

    if (!logged) {
      return { style: { borderRadius: '50%', ...todayBorder } as React.CSSProperties };
    }

    const inRange = isInAnyRange(dateStr);

    const prevStr = dayjs(date).subtract(1, 'day').format('YYYY-MM-DD');
    const nextStr = dayjs(date).add(1, 'day').format('YYYY-MM-DD');
    const prevConnected = isLoggedInRange(prevStr);
    const nextConnected = isLoggedInRange(nextStr);
    const hasNeighbor = prevConnected || nextConnected;

    if (!inRange || !hasNeighbor) {
      // Tier 1: standalone logged day
      return {
        selected: true,
        style: { borderRadius: '50%', backgroundColor: '#dbe4ff', color: '#364fc7', ...todayBorder } as React.CSSProperties,
      };
    }

    // Connected streak day — determine bar edges
    const first = !prevConnected;
    const last = !nextConnected;
    const isBoundary = first || last;

    return {
      selected: true,
      inRange: true,
      firstInRange: first,
      lastInRange: last,
      style: {
        borderRadius: '50%',
        backgroundColor: isBoundary ? '#5c7cfa' : '#bac8ff',
        color: '#fff',
        ...todayBorder,
      } as React.CSSProperties,
    };
  };

  return (
    <div className={styles.calendarCard}>
      <Calendar
        date={displayedMonth}
        onDateChange={(date) => setDisplayedMonth(new Date(date))}
        getDayProps={getDayProps}
        firstDayOfWeek={1}
        size="md"
        hideOutsideDates
        highlightToday={false}
        static
      />
    </div>
  );
};

export const TREND_MAP: Record<string, {
  text: string;
  icon: 'GreenIconsUpRight' | 'NeutralWithBGIcon' | 'RedDownWithBGIcon';
  comparison: string;
  bgClass: 'trendIconBgGreen' | 'trendIconBgNeutral' | 'trendIconBgRed';
}> = {
  LARGE_IMPROVEMENT: { text: 'Doing great', icon: 'GreenIconsUpRight', comparison: 'Than last week', bgClass: 'trendIconBgGreen' },
  SMALL_IMPROVEMENT: { text: 'Feeling better', icon: 'GreenIconsUpRight', comparison: 'Than last week', bgClass: 'trendIconBgGreen' },
  STEADY_HIGH: { text: 'Feeling great and productive', icon: 'NeutralWithBGIcon', comparison: 'Same as last week', bgClass: 'trendIconBgNeutral' },
  STEADY_MID: { text: 'Feeling steady', icon: 'NeutralWithBGIcon', comparison: 'Same as last week', bgClass: 'trendIconBgNeutral' },
  STEADY_LOW: { text: 'You are not feeling good', icon: 'NeutralWithBGIcon', comparison: 'Same as last week', bgClass: 'trendIconBgNeutral' },
  SMALL_DECLINE: { text: 'Feeling a bit lower', icon: 'RedDownWithBGIcon', comparison: 'Than last week', bgClass: 'trendIconBgRed' },
  LARGE_DECLINE: { text: 'The average mood decreased', icon: 'RedDownWithBGIcon', comparison: 'Than last week', bgClass: 'trendIconBgRed' },
};

export const MOOD_ICON_MAP: Record<string, {
  description: string;
  icon: 'MoodAwfulIcon' | 'MoodNotGoodIcon' | 'MoodFineIcon' | 'MoodGoodIcon' | 'MoodGreatIcon';
}> = {
  'Awful': { description: 'Not feeling well.', icon: 'MoodAwfulIcon' },
  'Not good': { description: 'Could be better.', icon: 'MoodNotGoodIcon' },
  'Fine': { description: 'Feeling okay.', icon: 'MoodFineIcon' },
  'Good': { description: 'Feeling productive.', icon: 'MoodGoodIcon' },
  'Great': { description: 'Feeling amazing.', icon: 'MoodGreatIcon' },
};

export const getMoodRange = (avg: number): string => {
  if (avg >= 4.5) return '4\u20135';
  if (avg >= 3.5) return '4\u20135';
  if (avg >= 2.5) return '3\u20134';
  if (avg >= 1.5) return '2\u20133';
  return '1\u20132';
};

export const getTrendInsight = (
  trendEnum: string,
  currentAvg: number | null,
  entries: any[] | undefined,
): string | null => {
  if (!currentAvg || !entries) return null;

  const loggedValues = entries
    .filter((e: any) => !e.is_skipped && e.mood_value != null)
    .map((e: any) => e.mood_value as number);

  if (loggedValues.length === 0) return null;

  const min = Math.min(...loggedValues);
  const max = Math.max(...loggedValues);
  const range = min === max ? `${min}` : `${min}\u2013${max}`;
  const avgRange = getMoodRange(currentAvg);

  switch (trendEnum) {
    case 'LARGE_IMPROVEMENT':
      return `This week was much better than the last few weeks, with more days in the ${avgRange} range.`;
    case 'SMALL_IMPROVEMENT':
      return `This week was slightly better than the last few weeks, with more days in the ${avgRange} range.`;
    case 'STEADY_HIGH':
      return `You\u2019ve been consistently feeling great, with days in the ${range} range.`;
    case 'STEADY_MID':
      return `Your mood has been steady this week, with most days in the ${range} range.`;
    case 'STEADY_LOW':
      return `Your mood has been lower than usual, with most days in the ${range} range. Consider checking in with yourself.`;
    case 'SMALL_DECLINE':
      return `This week was slightly lower than recent weeks, with more days in the ${avgRange} range.`;
    case 'LARGE_DECLINE':
      return `This week saw a noticeable dip compared to recent weeks, with days mostly in the ${avgRange} range.`;
    default:
      return null;
  }
};

const MoodSummaryCard: React.FC = () => {
  const apiClient = useApiClient();

  const { data: moodHistory } = useQuery({
    queryKey: ['moodHistorySummary'],
    queryFn: async () => {
      const res = await apiClient.api.getMoodHistoryApiV1MoodHistoryGet({ days: 7 });
      return res?.data;
    },
  });

  const stats = moodHistory?.statistics as any;
  const trendEnum: string = stats?.trend_enum || 'NO_DATA';
  const allTimeLabel: string | null = stats?.all_time_label ?? null;
  const currentAvg: number | null = stats?.current_period_average ?? null;

  const trend = TREND_MAP[trendEnum];
  const moodInfo = allTimeLabel ? MOOD_ICON_MAP[allTimeLabel] ?? null : null;
  const insight = getTrendInsight(trendEnum, currentAvg, moodHistory?.entries as any);

  if (trendEnum === 'NO_DATA' && !moodInfo) {
    return (
      <div className={styles.summaryCard}>
        <div className={styles.summaryHeader}>
          <span className={styles.summaryLabel}>SUMMARY</span>
          <Icon iconName="SmileIcon" width="1.25rem" height="1.25rem" />
        </div>
        <span className={styles.trendText}>Not enough data yet</span>
      </div>
    );
  }

  return (
    <div className={styles.summaryCard}>
      <div className={styles.summaryHeader}>
        <span className={styles.summaryLabel}>SUMMARY</span>
        <Icon iconName="SmileIcon" width="1.25rem" height="1.25rem" />
      </div>

      {trend ? (
        <>
          <div className={styles.summaryTrend}>
            <span className={styles.trendText}>{trend.text}</span>
            <div className={styles[trend.bgClass]}>
              <Icon iconName={trend.icon} width="1.5rem" height="1.5rem" />
            </div>
          </div>
          <span className={styles.trendSubtext}>{trend.comparison}</span>
        </>
      ) : (
        <span className={styles.trendSubtext}>Log mood regularly to see weekly trends</span>
      )}

      {insight && (
        <div className={styles.summaryInfoBox}>
          <p>{insight}</p>
        </div>
      )}

      {moodInfo && allTimeLabel && (
        <div className={styles.averageSection}>
          <span className={styles.averageLabel}>AVERAGE FOR ALL TIME</span>
          <div className={styles.averageContent}>
            <Icon
              iconName={moodInfo.icon}
              width="2rem"
              height="2rem"
              className={styles.averageMoodIcon}
            />
            <div className={styles.averageText}>
              <span className={styles.averageTitle}>{allTimeLabel}.</span>
              <span className={styles.averageDescription}>{moodInfo.description}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const YourProgress = () => {
  const apiClient = useApiClient();
  const [endorsementsDrawerDirection, setEndorsementsDrawerDirection] = useState<EndorsementDirection | null>(null);
  const [positiveImpactsDrawerOpen, setPositiveImpactsDrawerOpen] = useState(false);

  const { data: dashboardData, isLoading } = useQuery<DashboardResponse>({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const res = await apiClient.api.getDashboardDataApiV1DashboardGet();
      return res?.data;
    },
  });

  // Fetch progress data from dedicated progress API for streak info
  const { data: progressData } = useQuery({
    queryKey: ['dashboardProgress'],
    queryFn: async () => {
      const res = await apiClient.api.getProgressChartDataApiV1DashboardProgressGet();
      return res?.data;
    },
  });

  const { data: endorsementsGivenData } = useQuery({
    queryKey: ['insightsEndorsements', 'given'],
    queryFn: async () => {
      const res = await apiClient.api.getEndorsementsApiV1InsightsEndorsementsGet({ direction: 'given', limit: 1 });
      return res?.data;
    },
  });

  const { data: endorsementsReceivedData } = useQuery({
    queryKey: ['insightsEndorsements', 'received'],
    queryFn: async () => {
      const res = await apiClient.api.getEndorsementsApiV1InsightsEndorsementsGet({ direction: 'received', limit: 1 });
      return res?.data;
    },
  });

  const { data: milestonesData } = useQuery<MilestonesResponse>({
    queryKey: ['insightsMilestones'],
    queryFn: async () => {
      const res = await apiClient.api.getMilestonesApiV1InsightsMilestonesGet();
      return res?.data;
    },
  });

  // Check if ANY habit was logged on a specific date (using progress API)
  const isAnyHabitLoggedOnDate = (dateString: string): boolean => {
    return progressData?.daily_completion?.[dateString] === true;
  };

  // Check if a date falls within any streak range
  const isDateInStreakRange = (dateString: string): boolean => {
    const streakRanges: StreakRange[] = (progressData?.overall_stats as any)?.streak_ranges || [];
    if (streakRanges.length === 0) return false;
    
    const date = new Date(dateString);
    return streakRanges.some(range => {
      const start = new Date(range.start);
      const end = new Date(range.end);
      return date >= start && date <= end;
    });
  };

  if (isLoading) {
    return (
      <div className={styles.yourProgress}>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className={styles.yourProgress}>
        <div className={styles.emptyState}>
          <p>No data available</p>
        </div>
      </div>
    );
  }

  // Get 7-day rear-facing view: today + last 6 days
  const getStreakDaysCompletion = () => {
    const today = new Date();
    const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    const result = [];
    
    // 7 days: last 6 days + today (rear-facing)
    for (let i = 6; i >= 0; i--) {
      const day = new Date(today);
      day.setDate(today.getDate() - i);
      const dateString = day.toISOString().split('T')[0];
      const dayOfWeek = day.getDay();
      
      // Pill filled only if daily_completion[date] === true AND date falls within any streak_range
      const isLogged = isAnyHabitLoggedOnDate(dateString);
      const isInStreakRange = isDateInStreakRange(dateString);
      const isCompleted = isLogged && isInStreakRange;
      
      const isToday = i === 0;
      
      result.push({
        date: dateString,
        dayName: dayNames[dayOfWeek],
        isCompleted,
        isToday,
        formattedDate: isToday ? day.toLocaleDateString('en-US', { day: 'numeric', month: 'long' }) : null
      });
    }
    return result;
  };

  const streakDays = getStreakDaysCompletion();
  
  

  type OverallStats = {
    current_streak?: number;
    total_tracked?: number;
    [key: string]: any;
  };
  // Use progress API data for streak info (preferred) or fallback to dashboard data
  const progressOverallStats: OverallStats = (progressData?.overall_stats ?? {}) as OverallStats;
  const dashboardOverallStats: OverallStats = (dashboardData.progress_chart?.overall_stats ?? {}) as OverallStats;
  const currentStreak = progressOverallStats.current_streak ?? dashboardOverallStats.current_streak ?? 0;

  // Stats data - Real data from API
  const positiveImpacts = dashboardData.positive_changes_widget.total_logged || 0;
  const endorsementsGiven = endorsementsGivenData?.total_count ?? 0;
  const endorsementsReceived = endorsementsReceivedData?.total_count ?? 0;

  const milestoneHabits = milestonesData?.habits ?? [];

  const getMilestoneRingColor = (status: string) =>
    status === 'at_risk' ? '#FA5252' : '#4C6EF5';

  const getMilestoneIconBg = (level: number) => {
    switch (level) {
      case 1: return '#EDF2FF';
      case 2: return '#E3FAFC';
      case 3: return '#F3F0FF';
      case 4: return '#FFF4E6';
      default: return '#EDF2FF';
    }
  };

  const getMilestoneRingCol = (level: number) => {
    switch (level) {
      case 1: return 'hsla(228, 89%, 63%, 1)';
      case 2: return 'hsla(187, 80%, 42%, 1)';
      case 3: return '#7950F2';
      case 4: return '#FD7E14';
      default: return '#4C6EF5';
    }
  };

  const getNextLevelHint = (habit: typeof milestoneHabits[0]) => {
    if (!habit.next_level) return null;
    const logsReq = habit.next_level.requirements.find((r) => r.type === 'logs');
    if (logsReq && !logsReq.met) {
      return null;
    }
    const piReq = habit.next_level.requirements.find((r) => r.type === 'positive_impact');
    if (piReq && !piReq.met) return 'Log a positive impact';
    const endReq = habit.next_level.requirements.find((r) => r.type === 'endorsement_given');
    if (endReq && !endReq.met) return 'Endorse a teammate';
    return null;
  };

  const getAdoptionLabel = (status: string) =>
    status === 'at_risk' ? 'At risk' : 'On track';

  return (
    <>
    <div className={styles.yourProgress}>
      {/* First Row - Habit Adoption + Stats */}
      <div className={styles.firstRow}>
        {/* Habit Adoption Section */}
        <div className={styles.habitAdoptionSection}>
          <h3 className={styles.sectionTitle}>Habit adoption</h3>

          {milestoneHabits.length === 0 ? (
            <div className={styles.habitAdoptionEmpty}>
              <Icon iconName="NoTasksIcon" width="5rem" height="5rem" />
              <p>No habit adoption found</p>
            </div>
          ) : milestoneHabits.length === 1 ? (() => {
            const habit = milestoneHabits[0];
            const pct = habit.total_required > 0
              ? Math.round((habit.total_logs / habit.total_required) * 100)
              : 0;
            const ringColor = getMilestoneRingColor(habit.adoption_status);
            const hint = getNextLevelHint(habit);
            const levelNames = ['Explorer', 'Practitioner', 'Expert', 'Role model'];
            const milestoneIconNames = ['MilestonesLevel1', 'MilestonesLevel2', 'MilestonesLevel3', 'MilestonesLevel4'] as const;

            return (
              <>
                <div className={styles.singleHabitTop}>
                  <RingProgress
                    size={182}
                    thickness={10}
                    roundCaps
                    sections={[{ value: pct, color: ringColor }]}
                    label={
                      <Text size="xl" ta="center" style={{ fontSize: '2rem', fontWeight: 400 }}>
                        {pct}%
                      </Text>
                    }
                  />
                  <div className={styles.singleHabitInfo}>
                    <span className={styles.singleHabitName}>{habit.habit_name}</span>
                    <span className={styles.singleHabitStatus}>{getAdoptionLabel(habit.adoption_status)}</span>
                  </div>
                  <div className={styles.singleHabitTracks}>
                    <span className={styles.habitAdoptionColHeader}>TRACKS / TIMELINE</span>
                    <span className={styles.habitAdoptionTracksValue}>
                      {habit.total_logs}/{habit.total_required}
                    </span>
                    <span className={styles.habitAdoptionTracksSub}>
                      times tracked / {habit.days_remaining} day{habit.days_remaining !== 1 ? 's' : ''} left
                    </span>
                  </div>
                </div>

                <div className={styles.milestonesTimeline}>
                  {[1, 2, 3, 4].map((level, idx) => {
                    const iconBg = level <= habit.current_level ? getMilestoneIconBg(level) : '#F1F3F5';
                    const iconName = milestoneIconNames[idx];
                    let ringValue = 0;
                    if (level < habit.current_level) {
                      ringValue = 100;
                    } else if (level === habit.current_level && habit.next_level) {
                      const reqs = habit.next_level.requirements;
                      const avg = reqs.reduce((sum, r) => sum + Math.min(r.current / r.target, 1), 0) / reqs.length;
                      ringValue = Math.round(avg * 100);
                    }
                    const ringCol = level <= habit.current_level ? getMilestoneRingCol(level) : '#DEE2E6';

                    const hintForGap = level < 4 && level === habit.current_level && hint
                      ? hint
                      : null;

                    return (
                      <React.Fragment key={level}>
                        <div className={styles.milestonesTimelineLevel}>
                          <RingProgress
                            size={60}
                            thickness={4}
                            roundCaps
                            sections={[{ value: ringValue, color: ringCol }]}
                            label={
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                height: '100%',
                              }}>
                                <div style={{
                                  width: '2.55rem',
                                  height: '2.55rem',
                                  borderRadius: '50%',
                                  backgroundColor: iconBg,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}>
                                  <Icon iconName={iconName} width="1.6rem" height="1.6rem" />
                                </div>
                              </div>
                            }
                          />
                          <span className={styles.milestonesTimelineLevelTitle}>Level {level}</span>
                          <span className={styles.milestonesTimelineLevelName}>{levelNames[idx]}</span>
                        </div>

                        {idx < 3 && (
                          <div className={styles.milestonesTimelineConnector}>
                            <div className={styles.milestonesTimelineLine} />
                            {hintForGap && (
                              <span className={styles.milestonesTimelineHint}>{hintForGap}</span>
                            )}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </>
            );
          })() : (
            <>
              <div className={styles.habitAdoptionHeaders}>
                <span className={styles.habitAdoptionColHeader} style={{ width: '20rem' }}>HABIT</span>
                <span className={styles.habitAdoptionColHeader} style={{ flex: 1 }}>TRACKS / TIMELINE</span>
                <span className={styles.habitAdoptionColHeader} style={{ flex: 1 }}>MILESTONE</span>
              </div>
              <div className={styles.habitAdoptionList}>
                {milestoneHabits.map((habit) => {
                  const pct = habit.total_required > 0
                    ? Math.round((habit.total_logs / habit.total_required) * 100)
                    : 0;
                  const ringColor = getMilestoneRingColor(habit.adoption_status);
                  const levelIconBg = getMilestoneIconBg(habit.current_level);
                  const milestoneIconNames = ['MilestonesLevel1', 'MilestonesLevel2', 'MilestonesLevel3', 'MilestonesLevel4'] as const;
                  const milestoneIcon = milestoneIconNames[Math.min(habit.current_level - 1, 3)];
                  const hint = getNextLevelHint(habit);

                  return (
                    <div key={habit.user_habit_id} className={styles.habitAdoptionRow}>
                      <div className={styles.habitAdoptionHabitCol}>
                        <RingProgress
                          size={120}
                          thickness={8}
                          roundCaps
                          sections={[{ value: pct, color: ringColor }]}
                          label={
                            <Text size="xl" ta="center" style={{ fontSize: '2rem', fontWeight: 400 }}>
                              {pct}%
                            </Text>
                          }
                        />
                        <div className={styles.habitAdoptionHabitInfo}>
                          <span className={styles.habitAdoptionHabitName}>{habit.habit_name}</span>
                          <span className={styles.habitAdoptionStatus}>{getAdoptionLabel(habit.adoption_status)}</span>
                        </div>
                      </div>

                      <div className={styles.habitAdoptionTracksCol}>
                        <span className={styles.habitAdoptionTracksValue}>
                          {habit.total_logs}/{habit.total_required}
                        </span>
                        <span className={styles.habitAdoptionTracksSub}>
                          times tracked / {habit.days_remaining} day{habit.days_remaining !== 1 ? 's' : ''} left
                        </span>
                      </div>

                      <div className={styles.habitAdoptionMilestoneCol}>
                        <RingProgress
                          size={60}
                          thickness={4}
                          roundCaps
                          sections={[{
                            value: (() => {
                              const completedLevels = habit.current_level - 1;
                              const nextReqs = habit.next_level?.requirements;
                              const inLevelProgress = nextReqs
                                ? nextReqs.reduce((s, r) => s + Math.min(r.current / r.target, 1), 0) / nextReqs.length
                                : 0;
                              return Math.round(((completedLevels + inLevelProgress) / 4) * 100);
                            })(),
                            color: getMilestoneRingCol(habit.current_level),
                          }]}
                          label={
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '100%',
                              height: '100%',
                            }}>
                              <div style={{
                                width: '2.55rem',
                                height: '2.55rem',
                                borderRadius: '50%',
                                backgroundColor: levelIconBg,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}>
                                <Icon iconName={milestoneIcon} width="1.6rem" height="1.6rem" />
                              </div>
                            </div>
                          }
                        />
                        <div className={styles.habitAdoptionLevelInfo}>
                          <span className={styles.habitAdoptionLevelTitle}>Level {habit.current_level}</span>
                          <span className={styles.habitAdoptionLevelName}>{habit.current_level_name}</span>
                          {hint && <span className={styles.habitAdoptionLevelHint}>{hint}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Stats Cards */}
        <div className={styles.statsColumn}>
          <div className={styles.miniStatCard}>
            <div className={styles.miniStatHeader}>
              <span className={styles.miniStatLabel}>ENDORSEMENTS GIVEN</span>
              <Icon iconName="FingerUpIcon" width="1rem" height="1rem" />
            </div>
            <div className={styles.miniStatRow}>
              <div className={styles.miniStatValue}>{endorsementsGiven}</div>
              <button className={styles.viewMoreBtn} onClick={() => setEndorsementsDrawerDirection('given')}>View more</button>
            </div>
          </div>
          <div className={styles.miniStatCard}>
            <div className={styles.miniStatHeader}>
              <span className={styles.miniStatLabel}>ENDORSEMENTS RECEIVED</span>
              <Icon iconName="PersonIcon" width="1rem" height="1rem" />
            </div>
            <div className={styles.miniStatRow}>
              <div className={styles.miniStatValue}>{endorsementsReceived}</div>
              <button className={styles.viewMoreBtn} onClick={() => setEndorsementsDrawerDirection('received')}>View more</button>
            </div>
          </div>
          <div className={styles.miniStatCard}>
            <div className={styles.miniStatHeader}>
              <span className={styles.miniStatLabel}>POSITIVE IMPACTS</span>
              <Icon iconName="ChatIcon" width="1rem" height="1rem" />
            </div>
            <div className={styles.miniStatRow}>
              <div className={styles.miniStatValue}>{positiveImpacts}</div>
              <button className={styles.viewMoreBtn} onClick={() => setPositiveImpactsDrawerOpen(true)}>View more</button>
            </div>
          </div>
        </div>
      </div>

      {/* Second Row - Daily Streak + Calendar */}
      <div className={styles.firstRow}>
        <div className={styles.dailyStreakCard}>
          <h3 className={styles.sectionTitle}>Daily streak</h3>
          <div className={styles.streakContent}>
            <div className={styles.weekDaysSection}>
              <div className={styles.weekDays}>
                {streakDays.map((day) => (
                  <div key={day.date} className={styles.weekDay} >
                    <Icon
                      iconName={day.isCompleted ? "CheckedGreenIconCircle" : "NotCheckedGrayFullIconCircle"} 
                      width="2rem" 
                      height="2rem" 
                    />
                    <span className={`${styles.dayName} ${day.isToday ? styles.dayNameToday : ''}`}>{day.dayName}</span>
                    {day.isToday && day.formattedDate && (
                      <span className={styles.todayLabel}>Today, {day.formattedDate}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.streakBadge}>
              <Icon 
                iconName={currentStreak === 0 ? "KeepGoingGreyIcon" : "KeepDoingSmallIcon"} 
                width="4.8rem" 
                height="6.5rem" 
              />
              <span className={styles.streakText}>{currentStreak} days streak</span>
             <>
                <div className={styles.streakNudge}>Track habit today not to lose your streak</div>
              </>
     
            </div>
          </div>
        </div>
        <StreakCalendar />
      </div>


      {/* Fourth Row - How you feel */}
      <div className={styles.moodSection}>
        <h3 className={styles.sectionTitle}>How you feel</h3>
        <div className={styles.moodContent}>
          <MoodCard stretchHeight />
          <MoodSummaryCard />
        </div>
      </div>
    </div>

    <EndorsementsDrawer
      opened={endorsementsDrawerDirection !== null}
      onClose={() => setEndorsementsDrawerDirection(null)}
      direction={endorsementsDrawerDirection ?? 'given'}
    />
    <PositiveImpactsDrawer
      opened={positiveImpactsDrawerOpen}
      onClose={() => setPositiveImpactsDrawerOpen(false)}
    />
    </>
  );
};

// Goals & KPIs Component (for leaders only)
const GoalsKPIs = () => {
  // KPI data
  const kpiData = [
    { label: 'RFP RESPONSE RATE', value: '85%', target: '/ 90%', icon: 'ChartIcon' as const },
    { label: 'AVG CONTRACT VALUE', value: '£2.8M', target: '/ £2.5M', icon: 'DollarIcon' as const },
    { label: 'AVG RESPONSE QUALITY SCORE', value: '7.2', target: '/ 8.5', icon: 'StampIcon' as const },
    { label: 'PIPELINE VALUE', value: '£280M', target: '/ £250M', icon: 'DollarIcon' as const },
  ];

  // Behaviour selection by stream data
  const behaviourSelectionData = [
    { behaviour: 'Manage feedback ', value: 80 },
    { behaviour: 'Empathy', value: 100 },
    { behaviour: 'Understand', value: 20 },
    { behaviour: 'Promote inclusion', value: 90 },
  ];

  // KPIs with most habits logged
  const kpisHabitsData = [
    { kpi: 'RFR win rate', habitLogging: 75, kpiProgress: 100 },
    { kpi: 'Customer confi...', habitLogging: 45, kpiProgress: 65 },
    { kpi: 'Revenue', habitLogging: 25, kpiProgress: 70 },
    { kpi: 'Cycle time redu...', habitLogging: 60, kpiProgress: 75 },
    { kpi: 'Cost saving', habitLogging: 55, kpiProgress: 80 },
  ];

  // KPIs vs Behavior development scatter data
  const scatterData = [
    { color: '#4C6EF5', name: 'KPI vs Behaviour', data: [
      { x: 48, y: 42 }, { x: 52, y: 62 }, { x: 55, y: 58 }, { x: 57, y: 65 },
      { x: 58, y: 70 }, { x: 60, y: 68 }, { x: 62, y: 72 }, { x: 63, y: 75 },
      { x: 64, y: 70 }, { x: 65, y: 73 }, { x: 66, y: 76 }, { x: 67, y: 74 },
      { x: 68, y: 78 }, { x: 70, y: 75 }, { x: 72, y: 80 }, { x: 75, y: 78 },
      { x: 78, y: 82 },
    ]},
  ];

  // Staff retention vs Habit tracking
  const staffRetentionData = [
    { quarter: 'Q1', staffRetention: 85, activeHabitUsers: 20 },
    { quarter: 'Q2', staffRetention: 80, activeHabitUsers: 45 },
    { quarter: 'Q3', staffRetention: 70, activeHabitUsers: 90 },
    { quarter: 'Q4', staffRetention: 95, activeHabitUsers: 65 },
  ];

  return (
    <div className={styles.initiativeInsights}>
      {/* KPIs Section */}
      <div className={styles.kpisSection}>
        <span className={styles.kpisLabel}>KPIS</span>
        <div className={styles.kpiRowFour}>
          {kpiData.map((kpi, index) => (
            <QuickInsightCard key={index} title={kpi.label} icon={<Icon iconName={kpi.icon} width="1.25rem" height="1.25rem" />}>
              <div className={insightStyles.insightCard__row}>
                <span className={insightStyles.insightCard__mainValue}>{kpi.value}</span>
                <span className={insightStyles.insightCard__subtitle} style={{ marginLeft: '0.25rem' }}>{kpi.target}</span>
              </div>
            </QuickInsightCard>
          ))}
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className={styles.chartsRow}>
        <div className={styles.chartCard}>
          <h3 className={styles.chartTitle}>Behaviour selection by stream</h3>
          <BarChart
            h={280}
            data={behaviourSelectionData}
            dataKey="behaviour"
            series={[{ name: 'value', color: '#0C8599' }]}
            gridAxis="y"
            yAxisProps={{ domain: [0, 100] }}
            yAxisLabel="% of KPIs on target"
            barProps={{ radius: [4, 4, 0, 0] }}
          />
          <div className={styles.chartAxisLabel}>Behaviours</div>
        </div>
        <div className={styles.chartCard}>
          <h3 className={styles.chartTitle}>KPIs with most habits logged</h3>
          <BarChart
            h={280}
            data={kpisHabitsData}
            dataKey="kpi"
            series={[
              { name: 'habitLogging', color: '#1098AD', label: 'Habit logging rate' },
              { name: 'kpiProgress', color: '#4C6EF5', label: 'KPI progress' },
            ]}
            gridAxis="y"
            withLegend
            legendProps={{ verticalAlign: 'top', height: 50 }}
            yAxisProps={{ domain: [0, 100] }}
          />
          <div className={styles.chartAxisLabel}>KPIs with most habits logged</div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className={styles.chartsRow}>
        <div className={styles.chartCard}>
          <h3 className={styles.chartTitle}>KPIs vs Behavior development</h3>
          <div className={styles.scatterLabels}>
            <span className={styles.scatterLabelTopLeft}>Strong behaviour,<br/>slow KPI progress</span>
            <span className={styles.scatterLabelTopRight}>Hight behaviour,<br/>Strong KPI progress</span>
            <span className={styles.scatterLabelBottomLeft}>Need focus</span>
            <span className={styles.scatterLabelBottomRight}>Strong KPI progress but,<br/>not behaviour-led</span>
          </div>
          <ScatterChart
            h={250}
            data={scatterData}
            dataKey={{ x: 'x', y: 'y' }}
            xAxisLabel="KPI progress since last quarter"
            yAxisLabel="Avg habit progress %"
            xAxisProps={{ domain: [40, 80] }}
            yAxisProps={{ domain: [40, 80] }}
          />
        </div>
        <div className={styles.chartCard}>
          <h3 className={styles.chartTitle}>Staff retention vs Habit tracking</h3>
          <LineChart
            h={250}
            data={staffRetentionData}
            dataKey="quarter"
            series={[
              { name: 'staffRetention', color: '#4C6EF5', label: 'Staff retention %' },
              { name: 'activeHabitUsers', color: '#AE3EC9', label: 'Active habit users %' },
            ]}
            curveType="linear"
            withDots
            gridAxis="y"
            withLegend
            legendProps={{ verticalAlign: 'top', height: 50 }}
            yAxisProps={{ domain: [0, 100] }}
          />
        </div>
      </div>
    </div>
  );
};

const LeaderboardPage = () => {
  const apiClient = useApiClient();
  const [searchParams] = useSearchParams();
  const isEmbedMode = searchParams.get('embed') === 'true';
  const viewParam = searchParams.get('view');

  // Get user flow status to check if leader
  const { data: userFlowStatus } = useQuery<UserFlowStatusResponse>({
    queryKey: ['userFlowStatus'],
    queryFn: async () => {
      const res = await apiClient.api.getUserFlowStatusApiV1AuthUserFlowStatusGet();
      return res?.data;
    },
  });

  const isLeader = userFlowStatus?.app_role?.is_leader || false;
  const canViewInitiative = isLeader || userFlowStatus?.app_role?.is_sponsor || false;

  // In embed mode, default to 'leaderboard'. Otherwise default to 'progress'
  const defaultTab = isEmbedMode ? 'leaderboard' : 'progress';
  const initialTab = (viewParam === 'progress' || viewParam === 'leaderboard' || viewParam === 'initiative' || viewParam === 'goals') ? viewParam : defaultTab;

  const [activeTab, setActiveTab] = useState<'progress' | 'leaderboard' | 'initiative' | 'goals'>(initialTab as any);

  // Embed mode: no sidebar, no title, just the content
  if (isEmbedMode) {
    return (
      <div className={styles.embedContainer}>
        {activeTab === 'progress' && <YourProgress />}
        {activeTab === 'leaderboard' && <TeamProgressDashboard />}
      </div>
    );
  }

  const getPageTitle = () => {
    switch (activeTab) {
      case 'progress': return 'Progress';
      case 'leaderboard': return 'My Team';
      case 'initiative': return 'Initiative insights';
      case 'goals': return 'Organisation Insights';
      default: return 'Progress';
    }
  };

  return (
    <div className={layoutStyles.page}>
      <div className={layoutStyles.pageContainer}>
        {/* Sidebar with vertical tabs */}
        <aside className={layoutStyles.sidebar}>
          <nav className={layoutStyles.sidebarNav}>
            <button
              className={`${layoutStyles.sidebarTab} ${activeTab === 'progress' ? layoutStyles.active : ''}`}
              onClick={() => setActiveTab('progress')}
            >
              Progress
            </button>
            <button
              className={`${layoutStyles.sidebarTab} ${activeTab === 'leaderboard' ? layoutStyles.active : ''}`}
              onClick={() => setActiveTab('leaderboard')}
            >
              My Team
            </button>
            {canViewInitiative && (
              <button
                className={`${layoutStyles.sidebarTab} ${activeTab === 'initiative' ? layoutStyles.active : ''}`}
                onClick={() => setActiveTab('initiative')}
              >
                Initiative insights
              </button>
            )}
            {canViewInitiative && (
              <button
                className={`${layoutStyles.sidebarTab} ${activeTab === 'goals' ? layoutStyles.active : ''}`}
                onClick={() => setActiveTab('goals')}
              >
                Organisation Insights
              </button>
            )}
          </nav>
        </aside>

        {/* Main content */}
        <main className={layoutStyles.mainContent}>
          {activeTab !== 'leaderboard' && activeTab !== 'initiative' && activeTab !== 'goals' && (
            <h1 className={layoutStyles.pageTitle}>{getPageTitle()}</h1>
          )}
          <div className={layoutStyles.tabContent}>
            {activeTab === 'progress' && <YourProgress />}
            {activeTab === 'leaderboard' && <TeamProgressDashboard />}
            {activeTab === 'initiative' && <InitiativeInsights />}
            {activeTab === 'goals' && <OrganisationInsights />}
          </div>
        </main>
      </div>
    </div>
  );
};

export default LeaderboardPage;
